/**
 * Pricing Utilities - Stripe-Only Architecture
 * 
 * These utilities work with dynamic Stripe pricing data.
 * No hardcoded prices allowed - all values come from Stripe via Supabase.
 */

import type { StripePrice } from "@/hooks/useStripePricing";

/**
 * Format a price amount from cents to display string
 * @param amount - Price in cents (e.g., 3499 for €34.99)
 * @param currency - ISO currency code (e.g., 'eur', 'usd')
 */
export const formatPriceFromCents = (amount: number, currency: string = 'EUR'): string => {
  const formatter = new Intl.NumberFormat('en-EU', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return formatter.format(amount / 100);
};

/**
 * Get formatted price display from Stripe price data
 * Returns null if price is unavailable
 */
export const getPriceDisplay = (priceData: StripePrice | undefined | null): string | null => {
  if (!priceData || !priceData.amount) return null;
  return formatPriceFromCents(priceData.amount, priceData.currency);
};

/**
 * Check if a price is available
 */
export const isPriceAvailable = (priceData: StripePrice | undefined | null): boolean => {
  return Boolean(priceData && priceData.id && priceData.amount > 0);
};

/**
 * Get price ID safely, returns null if unavailable
 */
export const getPriceId = (priceData: StripePrice | undefined | null): string | null => {
  if (!priceData || !priceData.id) return null;
  return priceData.id;
};

/**
 * Calculate bundle price with discount
 * @param prices - Array of StripePrice objects
 * @param discountPercent - Discount percentage (e.g., 20 for 20% off)
 */
export const calculateBundlePrice = (
  prices: (StripePrice | undefined | null)[],
  discountPercent: number = 20
): { total: number; discount: number; bundlePrice: number } | null => {
  const validPrices = prices.filter(isPriceAvailable) as StripePrice[];
  
  if (validPrices.length !== prices.length) {
    return null; // Some prices are unavailable
  }
  
  const total = validPrices.reduce((sum, p) => sum + p.amount, 0);
  const discount = Math.round(total * (discountPercent / 100));
  const bundlePrice = total - discount;
  
  return { total, discount, bundlePrice };
};
